<template>
  <DonationAmount :donation_amount="state.donation_amount" />
  <div class="bg-white p-4 form-cont m-auto rounded-3 mb-5 dir-ltr">
    <form
      :action="state.redirectURI"
      class="paymentWidgets"
      :data-brands="dataBrands"
    ></form>
  </div>

  <DonationSuccessDialog
    :showModal="state.showDonationSuccessModal"
    :data="state.successDonationData"
    :heading="state.paymentSuccessHeading"
    :message="state.paymentSuccessMessage"
    :donationType="state.donationType"
    v-if="state.showDonationSuccessModal"
    @close="state.showDonationSuccessModal = false"
  />

  <DonationInProgressDialog
    :showModal="state.showInProgressModal"
    :data="state.progessDonationData"
    :status="state.progressStatus"
    :donationType="state.donationType"
    v-if="state.showInProgressModal"
    @close="state.showInProgressModal = false"
  />

  <DonationFailureDialog
    :showModal="state.showDonationFailureModal"
    :message="state.paymentFailureMessage"
    :heading="state.paymentFailureHeading"
    v-if="state.showDonationFailureModal"
    @close="state.showDonationFailureModal = false"
  />
</template>

<script setup>
import { reactive, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { defineAsyncComponent } from "vue";
import { useHyperpay } from "@/composables/useHyperpay";
import { DONATION_TYPE } from "@/services/enum.service";
import {
  getLocalValue,
  setLocalValue,
  removeLocalValue,
} from "@/hooks/useLocalStorage";

import DonationService from "@/services/donation.service";
import DonationSuccessDialog from "@/components/donate/DonationSuccessDialog";
import DonationFailureDialog from "@/components/donate/DonationFailureDialog";
import DonationAmount from "@/components/donate/DonationAmount";
import { extractPaymentFailureMessage } from "@/helpers/paymentStatusError";

const DonationInProgressDialog = defineAsyncComponent(() =>
  import("@/components/donate/DonationInProgressDialog.vue")
);

const route = useRoute();

const state = reactive({
  cardType: "credit_card",
  redirectURI: "",
  showDonationSuccessModal: false,
  paymentSuccessHeading: "",
  paymentSuccessMessage: "",
  successDonationData: {},
  donationType: DONATION_TYPE.CASE,
  paymentFailureMessage: "",
  paymentFailureHeading: '',
  showDonationFailureModal: false,
  resourcePath: "",
  paymentTransactionId: 0,
  showInProgressModal: false,
  progressStatus: 202,
  progessDonationData: {},
  donation_amount: null,
  isAmbassador: false,
  referenceId: null,
  paymentProgressMessage: "",
  integrity: getLocalValue("scriptIntegrity"),
});

const { dataBrands } = useHyperpay(state.cardType);

const setDonationAmount = () => {
  if (route.query.donation_amount) {
    state.donation_amount = route.query.donation_amount;
  }
};

const setDonationSuccessData = (data) => {
  if (!data) return;
  state.successDonationData = data.donation_success_data;
  state.paymentSuccessHeading=data.heading;
  state.paymentSuccessMessage=data.message;
  state.donationType = data?.donation_success_data?.type || DONATION_TYPE.CASE;
  state.showDonationSuccessModal = true;
};

const setDonationProgressData = (data, status) => {
  if (!data) return;
  state.progessDonationData = data;
  state.progressStatus = status;
  state.donationType = data.donation_info?.type || DONATION_TYPE.CASE;
  state.showInProgressModal = true;
};

const checkPaymentStatus = async () => {
  try {
    const { data } = await DonationService.getPaymentStatusWithRetry(
      state.paymentTransactionId,
      state.resourcePath
    );

    setLocalValue("paymentStatus", data.status);

    if (data.status === 202 || data.status ===400) {
      setDonationProgressData(data?.result, data?.status);
      setLocalValue("paymentId", state.paymentTransactionId);
      setLocalValue(state.paymentTransactionId, state.progessDonationData);
    } else if (data.status === 200) {
      if (data.donation_success_data) {
        const successData = {
        donation_success_data: data.donation_success_data,
        message: data.message,
        heading: data.heading
      };
        setDonationSuccessData(successData);
        setLocalValue("paymentId", state.paymentTransactionId);
        setLocalValue(state.paymentTransactionId, successData);
      }
    }

    removeLocalValue("donationModel");
    removeLocalValue("wallet");
  } catch (error) {
    state.paymentFailureMessage = extractPaymentFailureMessage(error);
    if (error?.response?.data?.errors?.length > 0) {
      state.paymentFailureHeading = error.response.data.heading;
    }
    state.showDonationFailureModal = true;
  }
};

// Initialize
setDonationAmount();

state.cardType = route.query.card_type;
if (route.query.reference_id) {
  state.isAmbassador = true;
  state.referenceId = route.query.reference_id;
}

state.redirectURI = process.env.VUE_APP_FE_URL + `/zakat-checkout`;

if (route.query.id && route.query.resourcePath) {
  state.resourcePath = route.query.resourcePath;
  state.paymentTransactionId = route.query.id;
  checkPaymentStatus();
}

onUnmounted(() => {
  const getLatestPayment = getLocalValue("paymentId");
  if (getLatestPayment) {
    removeLocalValue("paymentId");
    removeLocalValue(getLatestPayment.toString());
    removeLocalValue("paymentStatus");
  }
});
</script>

<style lang="scss" scoped>
.form-cont {
  width: fit-content;
}

.dir-ltr {
  direction: ltr;
}
</style>
